import React, { useState, useCallback, useContext } from 'react';
import './ShortsSection.css';
import { MyContext } from '../../../../Context/MyContext';
import { extractYoutubeVideoId, getYoutubeEmbedUrl, youtubeThumbnail } from '../../../../utils/youtubeEmbed';

/**
 * Skeleton for the shorts section
 */
const ShortsSkeleton = () => (
  <div className="shorts-scroll">
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="short-card" style={{ opacity: 0.6 }}>
        <div className="short-iframe-wrap home-skel-line" style={{ height: '400px', borderRadius: '12px' }} />
        <div className="short-info">
          <div className="home-skel-line" style={{ width: '80%', height: '14px', borderRadius: '4px', marginBottom: '8px' }} />
          <div className="home-skel-line" style={{ width: '50%', height: '10px', borderRadius: '4px' }} />
        </div>
      </div>
    ))}
  </div>
);

// ── Short Card ────────────────────────────────────────────────
const ShortCard = ({ short, isPlaying, onPlay }) => {
  const videoId = extractYoutubeVideoId(short.short_url);

  if (!videoId) return null;

  const embedUrl = getYoutubeEmbedUrl(videoId, { autoplay: true, loop: true });

  return (
    <div
      className={`short-card ${isPlaying ? 'short-playing' : ''}`}
      onClick={() => onPlay(short.id)}
    >
      <div className="short-iframe-wrap">
        {isPlaying ? (
          <iframe
            src={embedUrl}
            title={short.title}
            className="short-iframe"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <div className="short-placeholder">
            <img
              src={youtubeThumbnail(videoId, 'hqdefault')}
              alt={short.title}
              className="short-thumb"
            />

            <div className="short-overlay">
              <div className="short-play-btn">
  <svg viewBox="0 0 68 48" width="68" height="48">
    <path
      className="yt-play-bg"
      d="M66.52 7.21a8 8 0 0 0-5.63-5.66C55.66 0 34 0 34 0S12.34 0 7.11 1.55a8 8 0 0 0-5.63 5.66A83.5 83.5 0 0 0 0 24a83.5 83.5 0 0 0 1.48 16.79 8 8 0 0 0 5.63 5.66C12.34 48 34 48 34 48s21.66 0 26.89-1.55a8 8 0 0 0 5.63-5.66A83.5 83.5 0 0 0 68 24a83.5 83.5 0 0 0-1.48-16.79z"
      fill="#ff0000"
    />
    <path d="M45 24L27 14v20z" fill="#fff" />
  </svg>
</div>
            </div>
          </div>
        )}
      </div>

      <div className="short-info">
        <p className="short-title">{short.title}</p>
      </div>
    </div>
  );
};

// ── Main Section ──────────────────────────────────────────────
const ShortsSection = () => {
  const { youtubeShorts } = useContext(MyContext);
  const [playingId, setPlayingId] = useState(null);

  // Use a ref to track if data was ever loaded to avoid flickering
  const hasData = !!(youtubeShorts?.shorts?.length);

  // Only one video plays at a time
  const handlePlay = useCallback((id) => {
    setPlayingId(prev => (prev === id ? null : id));
  }, []);

  if (!youtubeShorts) return null;

  const { 
    section_title = 'Property Shorts', 
    section_subtitle = 'Experience properties from your home', 
    yt_channel_url = '#', 
    shorts = [] 
  } = youtubeShorts;

  return (
    <section className="shorts-section">
      <div className="shorts-inner">
        <div className="shorts-header">
          <div>
            <h2 className="shorts-title">{section_title}</h2>
            <p className="shorts-sub">{section_subtitle}</p>
          </div>
          <a href={yt_channel_url} target="_blank" rel="noreferrer" className="shorts-yt-btn">
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            View Channel
          </a>
        </div>

        {!hasData ? (
          <ShortsSkeleton />
        ) : (
          <div className="shorts-scroll">
            {shorts.map(short => (
              <ShortCard
                key={short.id}
                short={short}
                isPlaying={playingId === short.id}
                onPlay={handlePlay}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ShortsSection;